import { useState } from "react";
import { Lock, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { getClientId } from "@/lib/clientId";

/** Small gold "exclusive content" lock pill shown on locked cards/headers. */
export function LockBadge({ className = "", testId = "lock-badge" }) {
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 bg-[#C5A059] text-white text-[0.6rem] tracking-[0.24em] uppercase px-2.5 py-1 font-medium ${className}`}
    >
      <Lock className="w-3 h-3" aria-hidden="true" /> Exclusive
    </span>
  );
}

/**
 * Modal with two tabs: request access (name + email) and member sign-in
 * (email → magic link). Used wherever locked content is gated.
 */
export function AccessModal({ open, onClose, defaultTab = "request" }) {
  const [tab, setTab] = useState(defaultTab);
  const [form, setForm] = useState({ name: "", email: "" });
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState("");

  if (!open) return null;

  const submitRequest = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/member/access-request", { ...form, client_id: getClientId() });
      setDone(data.message || "Your request has been received.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Could not send request."));
    } finally { setBusy(false); }
  };

  const submitLink = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/member/request-link", { email });
      setDone(data.message || "If your access is approved, a sign-in link is on its way.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Could not send link."));
    } finally { setBusy(false); }
  };

  const switchTab = (t) => { setTab(t); setDone(""); };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-[#1A1F24]/60 backdrop-blur-sm px-6" data-testid="access-modal">
      <div className="relative w-full max-w-md bg-white border border-[#C5A059]/40 shadow-2xl">
        <button onClick={onClose} className="absolute top-4 right-4 text-[#5C6670] hover:text-[#1A1F24]" data-testid="access-modal-close" aria-label="Close">
          <X className="w-5 h-5" />
        </button>
        <div className="p-8 md:p-10">
          <div className="flex items-center gap-2 gv-label">
            <Lock className="w-4 h-4 text-[#C5A059]" /> Exclusive content
          </div>
          <div className="mt-5 flex border-b border-[#E5E5E5]">
            <button
              onClick={() => switchTab("request")}
              data-testid="access-tab-request"
              className={`pb-2 pr-6 text-sm tracking-[0.14em] uppercase ${tab === "request" ? "text-[#C5A059] border-b-2 border-[#C5A059]" : "text-[#5C6670]"}`}
            >Request access</button>
            <button
              onClick={() => switchTab("signin")}
              data-testid="access-tab-signin"
              className={`pb-2 px-6 text-sm tracking-[0.14em] uppercase ${tab === "signin" ? "text-[#C5A059] border-b-2 border-[#C5A059]" : "text-[#5C6670]"}`}
            >I'm a member</button>
          </div>

          {done ? (
            <p className="mt-8 text-[#5C6670] leading-relaxed" data-testid="access-modal-done">{done}</p>
          ) : tab === "request" ? (
            <form onSubmit={submitRequest} className="mt-6 space-y-5" data-testid="request-access-form">
              <p className="text-sm text-[#5C6670]">Request read-only access to Gemma's exclusive journals and the Space for Healing Together. Each request is reviewed personally.</p>
              <div>
                <label className="gv-label">Name</label>
                <input required maxLength={120} className="gv-input mt-2" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="request-name" />
              </div>
              <div>
                <label className="gv-label">Email</label>
                <input required type="email" maxLength={320} className="gv-input mt-2" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="request-email" />
              </div>
              <button type="submit" disabled={busy} className="gv-btn-gold w-full justify-center" data-testid="request-submit">
                {busy ? "Sending…" : "Request access"}
              </button>
            </form>
          ) : (
            <form onSubmit={submitLink} className="mt-6 space-y-5" data-testid="member-signin-form">
              <p className="text-sm text-[#5C6670]">Already approved? Enter your email and we'll send a one-time sign-in link.</p>
              <div>
                <label className="gv-label">Email</label>
                <input required type="email" maxLength={320} className="gv-input mt-2" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="signin-email" />
              </div>
              <button type="submit" disabled={busy} className="gv-btn-gold w-full justify-center" data-testid="signin-submit">
                {busy ? "Sending…" : "Email me a sign-in link"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

/** Locked-content placeholder shown in place of the hidden body/media. */
export function LockedNotice({ onRequest, label = "This is exclusive content" }) {
  return (
    <div className="border border-[#C5A059]/40 bg-[#FBF7EE] p-10 md:p-14 text-center" data-testid="locked-notice">
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#C5A059]/15 mb-5">
        <Lock className="w-6 h-6 text-[#C5A059]" aria-hidden="true" />
      </div>
      <h3 className="font-serif text-2xl md:text-3xl text-[#1A1F24]">{label}</h3>
      <p className="mt-3 text-[#5C6670] max-w-md mx-auto font-light leading-relaxed">
        Gemma shares this with approved members only. Request read-only access, or sign in if you're already a member.
      </p>
      <button onClick={onRequest} className="gv-btn-gold mt-7" data-testid="locked-request-btn">
        Request access
      </button>
    </div>
  );
}
